import {
  getDoc, getDocs,
  getDocFromCache, getDocsFromCache,
  type DocumentReference, type Query
} from 'firebase/firestore';

// SECURITY: Validate document reference before use
function isValidDocumentReference<T>(ref: DocumentReference<T> | null | undefined): ref is DocumentReference<T> {
  return ref !== null && ref !== undefined && typeof ref === 'object' && 'id' in ref;
}

// SECURITY: Validate query before use
function isValidQuery<T>(q: Query<T> | null | undefined): q is Query<T> {
  return q !== null && q !== undefined && typeof q === 'object';
}

// Prefer cache if available; fall back to server
export async function getDocCacheFirst<T>(ref: DocumentReference<T>) {
  // SECURITY: Validate input
  if (!isValidDocumentReference(ref)) {
    throw new Error('Invalid document reference');
  }
  
  try { 
    return await getDocFromCache(ref); 
  } catch (cacheError) {
    // SECURITY: Cache miss or error - fall through to server fetch
    // Don't log cache errors as they're expected when cache is empty
  }
  
  // SECURITY: Always validate ref again before server fetch
  if (!isValidDocumentReference(ref)) {
    throw new Error('Invalid document reference');
  }
  
  try {
    return await getDoc(ref);
  } catch (error) {
    // SECURITY: Re-throw with sanitized error message
    const errorMsg = error instanceof Error ? error.message : 'Failed to fetch document';
    throw new Error(`Document fetch failed: ${errorMsg.substring(0, 100)}`);
  }
}

export async function getDocsCacheFirst<T>(q: Query<T>) {
  // SECURITY: Validate input
  if (!isValidQuery(q)) {
    throw new Error('Invalid query');
  }
  
  try { 
    return await getDocsFromCache(q); 
  } catch (cacheError) {
    // SECURITY: Cache miss or error - fall through to server fetch
    // Don't log cache errors as they're expected when cache is empty
  }
  
  // SECURITY: Always validate query again before server fetch
  if (!isValidQuery(q)) {
    throw new Error('Invalid query');
  }
  
  try {
    return await getDocs(q);
  } catch (error) {
    // SECURITY: Re-throw with sanitized error message
    const errorMsg = error instanceof Error ? error.message : 'Failed to fetch documents';
    throw new Error(`Documents fetch failed: ${errorMsg.substring(0, 100)}`);
  }
}
